import { QueryClientProvider } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { ErrorInfo, JSX, ReactNode } from "react";
import { createBrowserRouter } from "react-router";
import { RouterProvider } from "react-router/dom";
import type { ApiClient } from "../api/ApiClient";
import { ApiClientProvider } from "../api/ApiContext";
import { PlatformAccessProvider } from "../features/platform/PlatformAccessContext";
import type { PlatformAccessMarker } from "../features/platform/platformAccess";
import { OfflineProvider } from "../offline/OfflineContext";
import type { OfflineServices } from "../offline/OfflineContext";
import { SyncProvider } from "../offline/SyncContext";
import { PlatformProvider } from "../platform/PlatformContext";
import type { PlatformServices } from "../platform/PlatformServices";
import { offersPurchasePaths } from "../platform/purchasePaths";
import { AppUpdateProvider } from "../pwa/AppUpdateContext";
import type { AppUpdateController } from "../pwa/AppUpdater";
import { InstallProvider } from "../pwa/InstallContext";
import type { InstallController } from "../pwa/installPrompt";
import { PushProvider } from "../pwa/PushContext";
import type { PushNotificationController } from "../pwa/pushNotifications";
import type { SyncController } from "../offline/SyncContext";
import { AuthProvider } from "../shared/auth/AuthContext";
import type { AuthService } from "../shared/auth/AuthService";
import { LegalLinksProvider } from "../shared/legal/LegalLinks";
import { ClientLogProvider } from "../shared/logging/ClientLogContext";
import type { ClientLog } from "../shared/logging/clientLog";
import type { LegalLink } from "../shared/legal/LegalLinks";
import type { PreferenceStore } from "../shared/preferences/PreferenceStore";
import { PreferencesProvider } from "../shared/preferences/Preferences";
import { SessionActionsProvider } from "../shared/session/SessionActions";
import { AnnouncerProvider } from "../shared/ui/Announcer";
import type { SessionActions } from "../shared/session/SessionActions";
import { ErrorBoundary } from "./ErrorBoundary";
import { createAppRoutes } from "./routes";
import { TenantSelectionProvider } from "../shared/session/tenantSelection";
import type { TenantSelectionStore } from "../shared/session/tenantSelection";

/**
 * Services created in the composition root (`main.tsx`) and injected into the component tree.
 */
export interface AppServices {
  /** Authentication. */
  readonly auth: AuthService;
  /** API client. */
  readonly api: ApiClient;
  /** Server-state cache. */
  readonly queryClient: QueryClient;
  /** Store for language and theme. */
  readonly preferences: PreferenceStore;
  /** Session-wide actions. */
  readonly session: SessionActions;
  /** Local storage for offline recording. */
  readonly offline: OfflineServices;
  /** Synchronization of the outbox. */
  readonly sync: SyncController;
  /** Updates of the installed application (service worker). */
  readonly updates: AppUpdateController;
  /** Push notifications of this device. */
  readonly push: PushNotificationController;
  /** Installation of the PWA. */
  readonly install: InstallController;
  /** Platform-specific capabilities (browser or native app). */
  readonly platform: PlatformServices;
  /** Tenant chosen by people with several employers (T-105). */
  readonly tenants: TenantSelectionStore;
  /** Links to the operator's legal pages shown in the app (ADR-0087 decision 2). */
  readonly legalLinks: readonly LegalLink[];
  /** Reports of errors and notable events to the operator's log (F-68). */
  readonly log: ClientLog;
  /** Remembers whether the signed-in person is operator staff, for the operator area (F-69). */
  readonly platformAccess: PlatformAccessMarker;
}

/** Properties of {@link AppProviders}. */
export interface AppProvidersProps {
  /** Injected services. */
  readonly services: AppServices;
  /** Child elements. */
  readonly children: ReactNode;
}

/**
 * Reports rendering errors without personal data (only the error type).
 *
 * @param log - The operator's log.
 * @param error - The error.
 * @param info - React error information.
 */
function reportRenderError(log: ClientLog, error: unknown, info: ErrorInfo): void {
  const name = error instanceof Error ? error.name : typeof error;
  console.error(`Rendering failed: ${name}`, info.componentStack);
  log.error("RenderFailed", { error });
}

/**
 * All context providers of the application, in dependency order.
 *
 * @param props - Component properties.
 * @returns The provider tree.
 */
export function AppProviders(props: AppProvidersProps): JSX.Element {
  const { services } = props;
  const providers = (
    <ClientLogProvider log={services.log}>
      <PreferencesProvider store={services.preferences} document={document}>
        <ErrorBoundary
          onError={(error, info) => {
            reportRenderError(services.log, error, info);
          }}
          fallback={() => <p role="alert">Die Anwendung konnte nicht geladen werden.</p>}
        >
          <PlatformProvider services={services.platform}>
            <AuthProvider service={services.auth}>
              <TenantSelectionProvider store={services.tenants}>
                <ApiClientProvider client={services.api}>
                  <QueryClientProvider client={services.queryClient}>
                    <OfflineProvider services={services.offline}>
                      <SyncProvider controller={services.sync}>
                        <AppUpdateProvider controller={services.updates}>
                          <PushProvider controller={services.push}>
                            <InstallProvider controller={services.install}>
                              <SessionActionsProvider actions={services.session}>
                                <LegalLinksProvider links={services.legalLinks}>
                                  <PlatformAccessProvider marker={services.platformAccess}>
                                    <AnnouncerProvider>{props.children}</AnnouncerProvider>
                                  </PlatformAccessProvider>
                                </LegalLinksProvider>
                              </SessionActionsProvider>
                            </InstallProvider>
                          </PushProvider>
                        </AppUpdateProvider>
                      </SyncProvider>
                    </OfflineProvider>
                  </QueryClientProvider>
                </ApiClientProvider>
              </TenantSelectionProvider>
            </AuthProvider>
          </PlatformProvider>
        </ErrorBoundary>
      </PreferencesProvider>
    </ClientLogProvider>
  );

  return providers;
}

/** Properties of {@link App}. */
export interface AppProps {
  /** Injected services. */
  readonly services: AppServices;
}

/**
 * Root component of the time tracking application.
 *
 * @param props - Component properties.
 * @returns The application.
 */
export function App(props: AppProps): JSX.Element {
  const [router] = useState(() =>
    createBrowserRouter(
      createAppRoutes({ purchasePaths: offersPurchasePaths(props.services.platform) }),
    ),
  );
  const app = (
    <AppProviders services={props.services}>
      <RouterProvider router={router} />
    </AppProviders>
  );

  return app;
}
